import React from 'react';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import CreateIcon from '@mui/icons-material/Create';
import HomeIcon from '@mui/icons-material/Home';
import PaymentIcon from '@mui/icons-material/Payment';

import {
  ActivityItem,
  ActivityFilters,
  ActivityResponse,
} from '../types/activity';

import { IActivityRepository } from './interfaces/IActivityRepository';

// Mock data for development - in production, this would come from an API
const MOCK_ACTIVITIES: ActivityItem[] = [
  {
    id: '1',
    message: 'You started this listing draft.',
    timeAgo: '1 day ago',
    type: 'listing',
    createdAt: new Date('2024-01-10T10:00:00Z'),
    icon: React.createElement(CreateIcon, {
      fontSize: 'small',
      style: { color: '#6b7280' },
    }),
  },
  {
    id: '2',
    message: 'Property photos uploaded successfully.',
    timeAgo: '2 days ago',
    type: 'listing',
    createdAt: new Date('2024-01-09T15:30:00Z'),
    icon: React.createElement(HomeIcon, {
      fontSize: 'small',
      style: { color: '#6b7280' },
    }),
  },
  {
    id: '3',
    message: 'Application received from John Smith.',
    timeAgo: '3 days ago',
    type: 'application',
    createdAt: new Date('2024-01-08T09:15:00Z'),
    icon: React.createElement(AssignmentIcon, {
      fontSize: 'small',
      style: { color: '#6b7280' },
    }),
  },
  {
    id: '4',
    message: 'Payment processed for screening package.',
    timeAgo: '4 days ago',
    type: 'payment',
    createdAt: new Date('2024-01-07T14:45:00Z'),
    icon: React.createElement(PaymentIcon, {
      fontSize: 'small',
      style: { color: '#6b7280' },
    }),
  },
  {
    id: '5',
    message: 'Maintenance request submitted for Unit 2A.',
    timeAgo: '5 days ago',
    type: 'maintenance',
    createdAt: new Date('2024-01-06T11:20:00Z'),
    icon: React.createElement(BuildIcon, {
      fontSize: 'small',
      style: { color: '#6b7280' },
    }),
  },
];

export class ActivityRepository implements IActivityRepository {
  private activities: ActivityItem[] = MOCK_ACTIVITIES;

  async getRecentActivities(
    filters?: ActivityFilters
  ): Promise<ActivityResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    let filteredActivities = [...this.activities];

    // Apply filters
    if (filters?.type) {
      filteredActivities = filteredActivities.filter(
        activity => activity.type === filters.type
      );
    }

    if (filters?.listingId) {
      // In a real implementation, this would filter by listing ID
      filteredActivities = filteredActivities.filter(
        activity => activity.type === 'listing'
      );
    }

    // Sort by creation date (newest first)
    filteredActivities.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });

    // Apply pagination
    const limit = filters?.limit || 10;
    const offset = filters?.offset || 0;
    const total = filteredActivities.length;
    const paginatedActivities = filteredActivities.slice(
      offset,
      offset + limit
    );

    return {
      activities: paginatedActivities,
      total,
      hasMore: offset + limit < total,
    };
  }

  async getActivitiesForListing(
    listingId: string,
    limit = 5
  ): Promise<ActivityResponse> {
    return this.getRecentActivities({
      listingId,
      limit,
      type: 'listing',
    });
  }
}

// Singleton instance
export const activityRepository = new ActivityRepository();
